import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SEO from '@/components/SEO';
import { Reveal } from '@/lib/useReveal';
import { BLOGS, BLOG_CATEGORIES } from '@/lib/blogs';
import { ArrowRight, Filter, Phone, Sparkles, Star } from 'lucide-react';
import { CONTACT_INFO } from '@/lib/content';

const VALID_SORTS = ['featured', 'newest'];

// Parse dates like "September 25, 2026" or ISO. Returns ms epoch or 0 if unparseable.
function parseBlogDate(dateStr) {
  if (!dateStr) return 0;
  const t = Date.parse(dateStr);
  return Number.isNaN(t) ? 0 : t;
}

export default function ArticlesPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const rawCat = searchParams.get('cat') || 'All';
  const cat = BLOG_CATEGORIES.includes(rawCat) ? rawCat : 'All';
  const rawSort = searchParams.get('sort') || 'featured';
  const sort = VALID_SORTS.includes(rawSort) ? rawSort : 'featured';

  const updateParams = (next) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(next).forEach(([key, value]) => {
      if (!value || value === 'All' || value === 'featured') params.delete(key);
      else params.set(key, value);
    });
    setSearchParams(params, { replace: false });
  };
  const setCat = (value) => updateParams({ cat: value });
  const setSort = (value) => updateParams({ sort: value });

  const featured = useMemo(() => BLOGS.find((b) => b.featured) || BLOGS[0], []);

  const rest = useMemo(() => {
    let list = BLOGS.filter((b) => b.slug !== featured.slug);
    if (cat !== 'All') list = list.filter((b) => b.category === cat);
    if (sort === 'newest') {
      list = [...list].sort((a, b) => parseBlogDate(b.date) - parseBlogDate(a.date));
    }
    return list;
  }, [cat, sort, featured.slug]);

  return (
    <main data-testid="articles-page" className="bg-white">
      <SEO
        title="Blog | Digital Marketing Insights & Case Studies"
        description="Digital marketing insights, SEO tips, web design trends, and client case studies from Brand Partners. Stay updated with the latest website launches and industry news."
        path="/blog"
      />

      <section className="relative pt-12 lg:pt-20 pb-12 bg-radial-blue overflow-hidden">
        <div className="absolute inset-0 bg-dot-grid opacity-40 pointer-events-none"/>
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8 relative grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <Reveal as="div" className="kicker"><span className="dot"/>News &amp; Insights</Reveal>
            <Reveal as="h1" delay={80} className="h-display mt-5 text-[44px] sm:text-[64px] lg:text-[80px]">
              Stories from the <span className="grad-text">field.</span>
            </Reveal>
          </div>
          <Reveal as="p" delay={140} className="lg:col-span-4 text-[17px] text-bp-slate leading-relaxed">
            Latest website launches, digital marketing strategies, and success stories from the Brand Partners team.
          </Reveal>
        </div>
      </section>

      {/* FEATURED */}
      <section className="pb-12">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8">
          <Reveal as="div">
            <Link to={`/blog/${featured.slug}`} className="grid lg:grid-cols-2 gap-10 group cursor-pointer items-center" data-testid={`featured-blog-${featured.slug}`}>
              <div className="overflow-hidden rounded-3xl">
                <img src={featured.img} alt={featured.title} className="w-full h-[340px] lg:h-[460px] object-cover transition-transform duration-700 group-hover:scale-[1.03]" loading="eager"/>
              </div>
              <div>
                <div className="inline-flex items-center gap-2 bg-bp-blue-50 text-bp-blue rounded-full px-3 py-1 text-[12px] font-semibold">Latest · {featured.category} · {featured.date}</div>
                <h2 className="h-display text-[32px] sm:text-[44px] mt-5 leading-[1.05] group-hover:text-bp-blue transition-colors">{featured.title}</h2>
                <p className="mt-5 text-[16.5px] leading-relaxed text-bp-slate max-w-xl">{featured.excerpt}</p>
                <div className="inline-flex items-center gap-2 mt-6 font-semibold text-bp-blue">Read article <ArrowRight size={14}/></div>
              </div>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* CATEGORY FILTER + SORT */}
      <div className="sticky top-[78px] z-20 bg-white/95 backdrop-blur-md border-y border-bp-slate-100 py-4">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8">
          <div className="flex items-center justify-between gap-4 mb-3">
            <div className="flex items-center gap-2">
              <Filter size={14} className="text-bp-slate-soft"/>
              <span className="text-[12px] font-semibold uppercase tracking-wider text-bp-slate-soft">Browse articles</span>
            </div>
            <div
              className="flex-shrink-0 inline-flex items-center gap-1 p-1 rounded-full bg-bp-slate-50 border border-bp-slate-100"
              role="tablist"
              aria-label="Sort articles"
              data-testid="articles-sort"
            >
              <button
                type="button"
                onClick={() => setSort('featured')}
                data-testid="articles-sort-featured"
                aria-pressed={sort === 'featured'}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12.5px] font-semibold transition-all ${
                  sort === 'featured'
                    ? 'bg-white text-bp-ink shadow-sm'
                    : 'text-bp-slate-soft hover:text-bp-ink'
                }`}
              >
                <Star size={13} className={sort === 'featured' ? 'text-bp-blue' : ''}/> Featured
              </button>
              <button
                type="button"
                onClick={() => setSort('newest')}
                data-testid="articles-sort-newest"
                aria-pressed={sort === 'newest'}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12.5px] font-semibold transition-all ${
                  sort === 'newest'
                    ? 'bg-white text-bp-ink shadow-sm'
                    : 'text-bp-slate-soft hover:text-bp-ink'
                }`}
              >
                <Sparkles size={13} className={sort === 'newest' ? 'text-bp-teal' : ''}/> Recently Added
              </button>
            </div>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="blog-filters">
            {BLOG_CATEGORIES.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCat(c)}
                data-testid={`blog-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                className={`flex-shrink-0 px-4 py-2 rounded-full text-[13.5px] font-semibold whitespace-nowrap transition-all ${
                  cat === c ? 'bg-bp-ink text-white shadow-md' : 'bg-bp-slate-50 text-bp-slate hover:bg-bp-blue-50 hover:text-bp-blue'
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* GRID */}
      <section className="py-14 lg:py-20 bg-bp-slate-50">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {rest.map((a) => (
            <Link key={a.slug} to={`/blog/${a.slug}`} className="card-soft overflow-hidden group flex flex-col cursor-pointer" data-testid={`blog-card-${a.slug}`}>
              <div className="overflow-hidden aspect-[4/3] bg-bp-slate-50">
                <img src={a.img} alt={a.title} className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.04]" loading="lazy" decoding="async"/>
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider">
                  <span className="text-bp-blue">{a.category}</span>
                  <span className="w-1 h-1 rounded-full bg-bp-slate-soft"/>
                  <span className="text-bp-slate-soft normal-case tracking-normal text-[12px] font-medium">{a.date}</span>
                </div>
                <h3 className="font-display font-bold text-bp-ink text-[19px] leading-snug mt-3 group-hover:text-bp-blue transition-colors">{a.title}</h3>
                <p className="mt-2.5 text-[14px] leading-relaxed text-bp-slate flex-1">{a.excerpt}</p>
                <span className="inline-flex items-center gap-1.5 mt-5 text-[13px] font-semibold text-bp-blue">Read More <ArrowRight size={13}/></span>
              </div>
            </Link>
          ))}
        </div>
        {rest.length === 0 && (
          <div className="text-center py-16 text-bp-slate">No articles in this category yet.</div>
        )}
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-8 text-center">
          <h2 className="h-display text-[34px] sm:text-[48px]">Ready to Be Our Next <span className="grad-text">Success Story?</span></h2>
          <p className="mt-5 text-[17px] text-bp-slate max-w-xl mx-auto">Let us build a website that generates leads and grows your business.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/contact" className="btn-primary"><Phone size={15}/> Book A Strategy Call</Link>
            <Link to="/portfolio" className="btn-secondary">View Portfolio <ArrowRight size={15}/></Link>
          </div>
          <div className="mt-6 text-[13px] text-bp-slate-soft">Or call us at <a href={`tel:${CONTACT_INFO.phoneTel}`} className="font-semibold text-bp-slate hover:text-bp-blue">{CONTACT_INFO.phone}</a></div>
        </div>
      </section>
    </main>
  );
}
